import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AxiosError } from 'axios';
import { ArrowLeft, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import SiteLogo from '@/components/common/SiteLogo';
import ThemeToggle from '@/components/common/ThemeToggle';
import { Button } from '@/components/ui/button';
import { useAccountAuth } from '@/app/hooks/useAccountAuth';
import { accountInputClass, FieldLabel, Notice } from '@/components/account/accountUi';

/**
 * Sign-in for the ORGANIZATION account (owner / admin).
 *
 * Separate from /auth, which signs in job candidates against a different realm.
 * A candidate who lands here is told where to go rather than being left with a
 * failed password.
 */

interface ApiErrorBody {
  message?: string;
  error?: string;
  code?: string;
}

function readErrorMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<ApiErrorBody>;
  const body = axiosError?.response?.data;
  if (axiosError?.response?.status === 403 && body?.code === 'AUTH_WRONG_PORTAL') {
    return 'This is a job-seeker account. Sign in through the careers portal instead.';
  }
  return body?.message || body?.error || fallback;
}

export default function AccountSignIn() {
  const { isAuthenticated, loading, signIn, completeMfaSignIn } = useAccountAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wrongPortal, setWrongPortal] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/account';

  if (!loading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handlePassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setWrongPortal(false);
    setSubmitting(true);
    try {
      const challenge = await signIn(email.trim(), password);
      if (challenge) {
        setPendingToken(challenge.pendingToken);
      } else {
        navigate(from, { replace: true });
      }
    } catch (caught) {
      const axiosError = caught as AxiosError<ApiErrorBody>;
      setWrongPortal(axiosError?.response?.data?.code === 'AUTH_WRONG_PORTAL');
      setError(readErrorMessage(caught, 'We could not sign you in. Check your email and password.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMfa = async (event: FormEvent) => {
    event.preventDefault();
    if (!pendingToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeMfaSignIn(pendingToken, code.trim());
      navigate(from, { replace: true });
    } catch (caught) {
      setError(
        readErrorMessage(caught, 'That code was not accepted. Try the current code from your app.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="flex h-14 items-center justify-between px-4 sm:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          ai2aim.ai
        </Link>
        <ThemeToggle />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-[26rem]"
        >
          <div className="mb-8 flex flex-col items-center text-center">
            <SiteLogo className="mb-5 h-10 w-10" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {pendingToken ? 'Two-factor verification' : 'Sign in to your account'}
            </h1>
            <p className="mt-2 max-w-[38ch] text-sm text-muted-foreground">
              {pendingToken
                ? 'Enter the 6-digit code from your authenticator app, or one of your recovery codes.'
                : 'Manage your plan, billing, and organization.'}
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-24px_rgba(15,23,42,0.35)]">
            {error && (
              <div className="mb-5">
                <Notice
                  tone={wrongPortal ? 'neutral' : 'bad'}
                  title={wrongPortal ? 'Wrong portal' : 'Sign-in failed'}
                  action={
                    wrongPortal ? (
                      <Button asChild size="sm" variant="outline">
                        <Link to="/auth">Careers portal</Link>
                      </Button>
                    ) : undefined
                  }
                >
                  {error}
                </Notice>
              </div>
            )}

            {pendingToken ? (
              <form onSubmit={handleMfa} className="space-y-5">
                <div>
                  <FieldLabel htmlFor="account-mfa-code">Verification code</FieldLabel>
                  <input
                    id="account-mfa-code"
                    className={`${accountInputClass} h-12 text-center font-mono text-lg tracking-[0.4em]`}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    inputMode="text"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={20}
                    placeholder="000000"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" size="lg" disabled={submitting || !code}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Verifying
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                      Verify and sign in
                    </>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingToken(null);
                    setCode('');
                    setError(null);
                  }}
                  className="w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  Use a different account
                </button>
              </form>
            ) : (
              <form onSubmit={handlePassword} className="space-y-5">
                <div>
                  <FieldLabel htmlFor="account-email">Work email</FieldLabel>
                  <input
                    id="account-email"
                    type="email"
                    className={accountInputClass}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div>
                  <FieldLabel htmlFor="account-password">Password</FieldLabel>
                  <input
                    id="account-password"
                    type="password"
                    className={accountInputClass}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={submitting || !email || !password}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Signing in
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-4 w-4" aria-hidden="true" />
                      Sign in
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Looking for a job application?{' '}
            <Link to="/auth" className="font-medium text-brand-cyan hover:underline">
              Careers portal
            </Link>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
